import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  SwapHoriz as MigrateIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import billingService from '../../services/billingService';
import HelpIcon from '../../components/help/HelpIcon';

const fmtBytes = (n) => {
  const v = typeof n === 'string' ? Number(n) : n;
  if (v == null || Number.isNaN(v)) return '—';
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB'];
  let x = v, i = 0;
  while (x >= 1024 && i < units.length - 1) { x /= 1024; i++; }
  return `${x.toFixed(x >= 10 ? 0 : 1)} ${units[i]}`;
};

const fmtNum = (n) => {
  const v = typeof n === 'string' ? Number(n) : n;
  if (v == null || Number.isNaN(v)) return '—';
  return v.toLocaleString();
};

const fmtMs = (n) => {
  const v = typeof n === 'string' ? Number(n) : n;
  if (v == null || Number.isNaN(v)) return '—';
  if (v < 1000) return `${v} ms`;
  return `${(v / 1000).toFixed(1)} s`;
};

/**
 * Read-only operational detail for one ClickHouse cluster. Four panels:
 * Disk usage / Active merges / Slow queries / Mutations. Populated from
 * {@code GET /clusters/{id}/health-detail} which hits system.* tables on
 * the cluster leader.
 */
const ClickHouseHealthDetailDialog = ({ open, clusterId, clusterName, onClose }) => {
  const [tab, setTab] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!clusterId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await billingService.clusterHealthDetail(clusterId);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Health detail failed');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [clusterId]);

  useEffect(() => {
    if (open) { setTab(0); load(); }
  }, [open, load]);

  const disk = useMemo(() => (Array.isArray(data?.diskUsageByTable) ? data.diskUsageByTable : []), [data]);
  const merges = useMemo(() => (Array.isArray(data?.activeMerges) ? data.activeMerges : []), [data]);
  const slow = useMemo(() => (Array.isArray(data?.slowQueries) ? data.slowQueries : []), [data]);
  const muts = useMemo(() => (Array.isArray(data?.mutations) ? data.mutations : []), [data]);
  const repQueue = useMemo(() => (Array.isArray(data?.replicationQueue) ? data.replicationQueue : []), [data]);

  const totalBytes = disk.reduce((s, r) => s + (Number(r.bytes) || 0), 0);
  const totalRows = disk.reduce((s, r) => s + (Number(r.rows) || 0), 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          Cluster health detail: {clusterName}
          <HelpIcon docKey="cluster_health_detail" />
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={load} startIcon={<RefreshIcon />}>Refresh</Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {data && (
          <Stack direction="row" spacing={3} sx={{ mb: 2, flexWrap: 'wrap' }}>
            <Summary label="Leader node" value={data.leaderNode} />
            {/* These figures are probed from the leader node only, so on a
                multi-shard cluster they cover the leader's shard, not the whole
                cluster — labelled honestly rather than as a cluster total. */}
            <Summary label="Data size (leader shard)" value={fmtBytes(totalBytes)} />
            <Summary label="Rows (leader shard)" value={fmtNum(totalRows)} />
            <Summary label="Tables" value={disk.length} />
            <Summary label="Active merges" value={merges.length >= 50 ? '50+' : merges.length} color={merges.length > 0 ? 'warning.main' : undefined} />
            <Summary label="Pending replication" value={repQueue.length >= 50 ? '50+' : repQueue.length} color={repQueue.length > 0 ? 'warning.main' : undefined} />
            <Summary label="In-flight mutations" value={muts.length >= 50 ? '50+' : muts.length} color={muts.length > 0 ? 'warning.main' : undefined} />
          </Stack>
        )}

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label={`Disk usage (${disk.length})`} />
          <Tab label={`Active merges (${merges.length >= 50 ? '50+' : merges.length})`} />
          <Tab label={`Slow queries (${slow.length >= 10 ? '10+' : slow.length})`} />
          <Tab label={`Mutations (${muts.length >= 50 ? '50+' : muts.length})`} />
          <Tab label={`Replication queue (${repQueue.length >= 50 ? '50+' : repQueue.length})`} />
        </Tabs>

        {tab === 0 && <DiskTable rows={disk} clusterId={clusterId} />}
        {tab === 1 && <MergesTable rows={merges} />}
        {tab === 2 && <SlowQueriesTable rows={slow} />}
        {tab === 3 && <MutationsTable rows={muts} />}
        {tab === 4 && <ReplicationTable rows={repQueue} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const Summary = ({ label, value, color }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h6" sx={{ color }}>{value ?? '—'}</Typography>
  </Box>
);

const EmptyRow = ({ message }) => (
  <Box sx={{ textAlign: 'center', py: 4 }}>
    <Typography variant="body2" color="text.secondary">{message}</Typography>
  </Box>
);

const DiskTable = ({ rows, clusterId }) => {
  const [recipe, setRecipe] = React.useState(null);
  const [recipeLoading, setRecipeLoading] = React.useState(false);
  const [recipeError, setRecipeError] = React.useState(null);
  const [recipeOpen, setRecipeOpen] = React.useState(false);

  const requestRecipe = async (database, table) => {
    setRecipeOpen(true);
    setRecipeLoading(true);
    setRecipeError(null);
    setRecipe(null);
    try {
      const res = await billingService.schemaMigrationRecipe(clusterId, database, table);
      setRecipe(res.data);
    } catch (e) {
      setRecipeError(e?.response?.data?.error || e.message || 'Recipe generation failed');
    } finally {
      setRecipeLoading(false);
    }
  };

  if (rows.length === 0) return <EmptyRow message="No tables on this cluster yet." />;
  return (
    <>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Database</TableCell>
            <TableCell>Table</TableCell>
            <TableCell>Engine</TableCell>
            <TableCell align="right">Size on disk</TableCell>
            <TableCell align="right">Rows</TableCell>
            <TableCell align="right">Active parts</TableCell>
            <TableCell>Last modified</TableCell>
            <TableCell>Migrate</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => {
            const engine = r.engine || '—';
            const isReplicated = engine.startsWith('Replicated');
            const isMergeTreeFamily = /MergeTree/.test(engine);
            return (
              <TableRow key={`${r.database}.${r.table}.${i}`}>
                <TableCell>{r.database}</TableCell>
                <TableCell>{r.table}</TableCell>
                <TableCell>
                  <Chip size="small"
                        color={isReplicated ? 'success' : isMergeTreeFamily ? 'default' : 'info'}
                        label={engine}
                        sx={{ fontSize: 10 }} />
                </TableCell>
                <TableCell align="right">{fmtBytes(r.bytes)}</TableCell>
                <TableCell align="right">{fmtNum(r.rows)}</TableCell>
                <TableCell align="right">{fmtNum(r.parts_active)}</TableCell>
                <TableCell><small>{r.last_modified}</small></TableCell>
                <TableCell>
                  {isMergeTreeFamily && !isReplicated && (
                    <Tooltip title="Generate a migration recipe to convert this table to the Replicated variant">
                      <span>
                        <button
                          onClick={() => requestRecipe(r.database, r.table)}
                          style={{
                            background: 'none', border: '1px solid rgba(127,127,127,0.4)',
                            color: 'inherit', padding: '2px 6px', borderRadius: 4,
                            cursor: 'pointer', fontSize: 11,
                          }}>
                          <MigrateIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.25 }} />
                          Migrate
                        </button>
                      </span>
                    </Tooltip>
                  )}
                  {isReplicated && (
                    <Chip size="small" color="success" variant="outlined" label="already replicated"
                          sx={{ fontSize: 10 }} />
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <MigrationRecipeDialog
        open={recipeOpen}
        onClose={() => setRecipeOpen(false)}
        loading={recipeLoading}
        error={recipeError}
        recipe={recipe}
      />
    </>
  );
};

const MigrationRecipeDialog = ({ open, onClose, loading, error, recipe }) => {
  const copy = (sql) => {
    try { navigator.clipboard.writeText(sql); } catch { /* ignore */ }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          Migration recipe
          <HelpIcon docKey="schema_migration_recipe" />
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {recipe && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>{recipe.database}.{recipe.table}</strong>:
              convert <code>{recipe.currentEngine}</code> → <code>{recipe.targetEngine}</code>
              {' '}· {recipe.rowCount >= 0 ? Number(recipe.rowCount).toLocaleString() + ' rows' : 'row count unknown'}
            </Alert>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Safety checklist</strong>
              <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
                {(recipe.safetyChecklist || []).map((c, i) => (
                  <li key={i}><Typography variant="body2">{c}</Typography></li>
                ))}
              </Box>
            </Alert>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Run each step manually (via clickhouse-client or SSH). We intentionally don\'t auto-execute these — RENAME and DROP need your eyes on.
            </Typography>
            {(recipe.steps || []).map((s) => (
              <Paper key={s.order} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Chip size="small" label={`Step ${s.order}`} color="primary" />
                  <Typography variant="subtitle2">{s.name}</Typography>
                  <Box sx={{ flex: 1 }} />
                  <Tooltip title="Copy SQL">
                    <button
                      onClick={() => copy(s.sql)}
                      style={{
                        background: 'none', border: '1px solid rgba(127,127,127,0.4)',
                        color: 'inherit', padding: '2px 8px', borderRadius: 4,
                        cursor: 'pointer', fontSize: 11,
                      }}>
                      <CopyIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.25 }} />
                      Copy
                    </button>
                  </Tooltip>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {s.description}
                </Typography>
                <Box component="pre" sx={{
                  m: 0, p: 1.5,
                  background: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                  borderRadius: 1,
                  fontFamily: 'ui-monospace, Menlo, monospace',
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  maxHeight: 300,
                  overflow: 'auto',
                }}>
                  {s.sql}
                </Box>
              </Paper>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const MergesTable = ({ rows }) => (
  rows.length === 0 ? <EmptyRow message="No active merges. Cluster is idle." /> : (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Table</TableCell>
          <TableCell align="right">Elapsed</TableCell>
          <TableCell align="right">Progress</TableCell>
          <TableCell align="right">Parts</TableCell>
          <TableCell align="right">Size</TableCell>
          <TableCell align="right">Memory</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={i}>
            <TableCell>{r.database}.{r.table}</TableCell>
            <TableCell align="right">{Number(r.elapsed || 0).toFixed(1)} s</TableCell>
            <TableCell align="right">{(Number(r.progress || 0) * 100).toFixed(1)} %</TableCell>
            <TableCell align="right">{fmtNum(r.num_parts)}</TableCell>
            <TableCell align="right">{fmtBytes(r.total_size_bytes_compressed)}</TableCell>
            <TableCell align="right">{fmtBytes(r.memory_usage)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
);

const SlowQueriesTable = ({ rows }) => (
  rows.length === 0 ? <EmptyRow message="No slow queries in the last hour. query_log may be disabled if you expected entries here." /> : (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>When</TableCell>
          <TableCell>User</TableCell>
          <TableCell align="right">Duration</TableCell>
          <TableCell align="right">Rows read</TableCell>
          <TableCell align="right">Bytes read</TableCell>
          <TableCell align="right">Memory</TableCell>
          <TableCell>Query</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={i}>
            <TableCell><small>{r.event_time}</small></TableCell>
            <TableCell>{r.user}</TableCell>
            <TableCell align="right">{fmtMs(r.query_duration_ms)}</TableCell>
            <TableCell align="right">{fmtNum(r.read_rows)}</TableCell>
            <TableCell align="right">{fmtBytes(r.read_bytes)}</TableCell>
            <TableCell align="right">{fmtBytes(r.memory_usage)}</TableCell>
            <TableCell>
              <Tooltip title={r.query || ''} placement="top-start">
                <Box sx={{
                  maxWidth: 420,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: 12,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {r.query}
                </Box>
              </Tooltip>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
);

const MutationsTable = ({ rows }) => (
  rows.length === 0 ? <EmptyRow message="No in-flight mutations. Schema changes are settled." /> : (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Table</TableCell>
          <TableCell>Mutation ID</TableCell>
          <TableCell>Command</TableCell>
          <TableCell align="right">Parts to do</TableCell>
          <TableCell>Created</TableCell>
          <TableCell>Last fail reason</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={i}>
            <TableCell>{r.database}.{r.table}</TableCell>
            <TableCell><small>{r.mutation_id}</small></TableCell>
            <TableCell><Box sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.command}</Box></TableCell>
            <TableCell align="right">{fmtNum(r.parts_to_do)}</TableCell>
            <TableCell><small>{r.create_time}</small></TableCell>
            <TableCell>{r.latest_fail_reason
              ? <Chip size="small" color="error" label={String(r.latest_fail_reason).slice(0, 60)} />
              : <Chip size="small" label="running" />}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
);

const ReplicationTable = ({ rows }) => (
  rows.length === 0 ? <EmptyRow message="Replication queue empty. (Only meaningful on ReplicatedMergeTree clusters.)" /> : (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Table</TableCell>
          <TableCell>Type</TableCell>
          <TableCell>Created</TableCell>
          <TableCell align="right">Retries</TableCell>
          <TableCell>Last exception</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={i}>
            <TableCell>{r.database}.{r.table}</TableCell>
            <TableCell>{r.type}</TableCell>
            <TableCell><small>{r.create_time}</small></TableCell>
            <TableCell align="right">{fmtNum(r.num_tries)}</TableCell>
            <TableCell>{r.last_exception
              ? <Chip size="small" color="error" label={String(r.last_exception).slice(0, 80)} />
              : '—'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
);

export default ClickHouseHealthDetailDialog;
